import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Switch,
  ActivityIndicator,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing, typography } from '../../theme';
import { supabase } from '../../lib/supabase';
import { Vendor } from '../../types/vendor';

interface OrderSettingsSectionProps {
  vendor: Vendor;
  onUpdate?: () => void;
}

export function OrderSettingsSection({ vendor, onUpdate }: OrderSettingsSectionProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing settings from vendor metadata
  useEffect(() => {
    if (vendor?.metadata) {
      const metadata = vendor.metadata as { auto_accept_orders?: boolean };
      setAutoAcceptOrders(metadata.auto_accept_orders ?? false);
    }
  }, [vendor?.metadata]);

  const handleToggle = (value: boolean) => {
    setAutoAcceptOrders(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!vendor?.id) return;
    
    setSaving(true);
    try {
      const currentMetadata = (vendor.metadata as Record<string, unknown>) || {};
      const updatedMetadata = {
        ...currentMetadata,
        auto_accept_orders: autoAcceptOrders,
      };

      const { error } = await supabase
        .from('vendors')
        .update({ metadata: updatedMetadata })
        .eq('id', vendor.id);

      if (error) throw error;

      setHasChanges(false);
      onUpdate?.();
    } catch (err) {
      console.error('Error saving order settings:', err);
    } finally {
      setSaving(false);
    }
  };

  // Check if store has hours configured
  const hasStoreHours = vendor?.delivery_hours && Object.keys(vendor.delivery_hours).length > 0;

  return (
    <View style={styles.container}>
      <TouchableOpacity 
        style={styles.settingRow}
        onPress={() => handleToggle(!autoAcceptOrders)}
      >
        <View style={styles.settingInfo}>
          <View style={styles.iconContainer}>
            <Feather name="check-circle" size={18} color={colors.primary} />
          </View>
          <View style={styles.textContainer}>
            <Text style={styles.settingTitle}>Auto-accept orders</Text>
            <Text style={styles.settingDescription}>
              Automatically accept new orders during store hours
            </Text>
          </View>
        </View>
        <Switch
          value={autoAcceptOrders}
          onValueChange={handleToggle}
          trackColor={{ false: colors.border, true: colors.primaryLight }}
          thumbColor={autoAcceptOrders ? colors.primary : colors.textLight}
        />
      </TouchableOpacity>

      {autoAcceptOrders && !hasStoreHours && (
        <View style={styles.warningBox}>
          <Feather name="alert-circle" size={14} color={colors.warning} />
          <Text style={styles.warningText}>
            Set store hours above for auto-accept to work correctly
          </Text>
        </View>
      )}

      {autoAcceptOrders && (
        <View style={styles.infoBox}>
          <Feather name="info" size={14} color={colors.textLight} />
          <Text style={styles.infoText}>
            Orders placed during your store hours will be automatically accepted. 
            Orders outside store hours will still require manual review.
          </Text>
        </View>
      )}

      <TouchableOpacity 
        style={[styles.saveButton, !hasChanges && styles.saveButtonDisabled]} 
        onPress={handleSave}
        disabled={saving || !hasChanges}
      >
        {saving ? (
          <ActivityIndicator size="small" color={colors.surface} />
        ) : (
          <Text style={styles.saveButtonText}>Save Settings</Text>
        )}
      </TouchableOpacity>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    paddingHorizontal: spacing.lg,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    padding: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: spacing.md,
  },
  iconContainer: {
    marginRight: spacing.sm,
  },
  textContainer: {
    flex: 1,
  },
  settingTitle: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 12,
    color: colors.textLight,
    lineHeight: 16,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.warningLight || `${colors.warning}15`,
    padding: spacing.sm,
    borderRadius: 6,
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    color: colors.warning,
    lineHeight: 16,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.backgroundSecondary,
    padding: spacing.sm,
    borderRadius: 6,
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    color: colors.textLight,
    lineHeight: 16,
  },
  saveButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.success,
    paddingVertical: 14,
    borderRadius: 8,
    marginTop: spacing.lg,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 17,
    color: colors.surface,
    fontWeight: '600',
  },
});

